import { AsyncPipe, DOCUMENT, NgClass } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { ProductFactory, QuoteModel, Sowo } from '@bytel/bytel-sales';
import { Oauth2Service } from '@bytel/checkout';

import { REINSURANCE_CHOICES } from '@base/common/constants';
import { ModalService } from '@base/common/services/modal.service';

import { AppService } from '@services/app.services';
import { MainCartService } from '@services/main-cart.service';
import { MessageService } from '@services/message.service';
import { RecallService } from '@services/recall.service';
import { StepperService } from '@services/stepper.service';

import { BasicObject } from '@interfaces/checkout.interface';
import { IInitialConfig } from '@interfaces/config.interface';

import { PartnerExclusivityErrorModalComponent } from '@shared/partner-exclusivity-error-modal/partner-exclusivity-error-modal.component';
import { ReinsuranceComponent } from '@shared/reinsurance/reinsurance.component';
import { StepperComponent } from '@shared/stepper/stepper.component';
import { Step } from '@shared/stepper/steps/step';
import { TicketComponent } from '@shared/ticket/ticket.component';
import { ToasterListComponent } from '@shared/toaster-list/toaster-list.component';

import { Observable, filter } from 'rxjs';

import { initialConfig } from '../environments/environment';
import { KonamiDirective } from './common/directives/konami.directive';
import { MocksHandlerComponent } from './mocks-handler/mocks-handler.component';
import { CreditAccordion } from './shared/credit-accordion/credit-accordion.component';

@Component({
    selector: 'checkout-app',
    templateUrl: './app.component.html',
    styleUrl: 'app.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        KonamiDirective,
        NgClass,
        StepperComponent,
        RouterOutlet,
        ReinsuranceComponent,
        CreditAccordion,
        TicketComponent,
        MocksHandlerComponent,
        AsyncPipe,
        ToasterListComponent,
    ],
})
export class AppComponent implements OnInit, AfterViewInit {
    @ViewChild('modal', {
        read: ViewContainerRef,
    })
    modal: ViewContainerRef;
    public debugModeActive: boolean = false;
    public showMock: boolean = false;
    public type: string;
    public title: string;
    public currentStep: Step;
    public configuration: BasicObject = {
        [REINSURANCE_CHOICES.CHECKED_REVIEW]: 'tri-hands-helping',
        [REINSURANCE_CHOICES.FREE_SHIPPING]: 'tri-truck-speed',
        [REINSURANCE_CHOICES.SECURE_PAYMENT]: 'tri-credit-cards',
        [REINSURANCE_CHOICES.CUSTOMER_SERVICE]: 'tri-shooting-star',
    };
    public isSowo: boolean = false;
    public showSkeleton$: Observable<boolean>;
    public showTicket$: Observable<boolean>;
    public showBackToStepOneButton: boolean = false;

    public _initialConfig: IInitialConfig;

   
    private cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private oauth2Service: Oauth2Service = inject(Oauth2Service);
    private recallService: RecallService = inject(RecallService);
    private cartService: MainCartService = inject(MainCartService);
    private stepperService: StepperService = inject(StepperService);
    private messageService: MessageService = inject(MessageService);
    private router: Router = inject(Router);
    private appService: AppService = inject(AppService);
    private modalService: ModalService = inject(ModalService);
    @Inject(DOCUMENT) private document: Document = inject(DOCUMENT);

    public constructor(
    ) {
        this._initialConfig = initialConfig();
        this.showMock = this.appService.isTestEnv();

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((e) => {
            const event: NavigationEnd = e as NavigationEnd;
            this.showBackToStepOneButton = !(event.url.includes('/panier') || event.url === '/');
        });
    }

    public ngOnInit(): void {
        this.showSkeleton$ = this.appService.skeletonObservable;
        this.showTicket$ = this.appService.showTicketObservable;
        this.debugModeActive = this.appService.debugMode.active;
        this.cartService.cartQuoteSubscription.subscribe(() => {
            const quote: QuoteModel = this.cartService.getQuote();
            const sowo: Sowo = quote.getProductByType(Sowo);
            this.isSowo = ProductFactory.Is(sowo, Sowo, true);
        });
        this.oauth2Service.afterLogout.subscribe(() => {
            this.cartService.deleteCartIds();
        });
        this.currentStep = this.stepperService.currentStep;
        this.stepperService.currentStepSubject.subscribe((step: Step) => {
            const quote: QuoteModel = this.cartService.getQuote();
            if (!quote.isRenewal() && this.cartService.isBankingCartExpired()) {
                this.cartService.removeQuote();
                this.cartService.addQuote();
                this.cartService.save();
                this.messageService.openModal(PartnerExclusivityErrorModalComponent, {
                    isExpiredCart: true,
                });
            }
            this.currentStep = step;
        });
        this.recallService.initRouteHandler().subscribe();
    }

    public ngAfterViewInit(): void {
        this.modalService.modalViewComponentRef = this.modal;
        this.appService.modalSubject.next(null);
        this.appService.modalReady = true;
        this.cdRef.detectChanges();
    }

    public konami(): void {
        for (const key in this.configuration) {
            const element: HTMLElement = this.document.getElementById(`reinsurance-${key.toLowerCase()}`);
            if (element) {
                element.className = this.configuration[key];
            }
        }
    }

    public async goToHome(): Promise<void> {
        await this.router.navigateByUrl('/panier');
    }
}
